import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import GlobalStyles from "../../../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../../../_layout";
import { useModalKeyboardShortcuts } from "../../../platform/guards/modalKeyboardShortcuts";
import { normalizeNutritionPlanExportName } from "../nutritionPlanLibraryExport";

const styles = StyleSheet.create({
  modalView: {
    alignSelf: "center",
    backgroundColor: Colors.whiteColor,
    borderColor: "rgba(15, 23, 42, 0.08)",
    borderRadius: 22,
    borderWidth: 1,
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.14)",
    maxWidth: 420,
    paddingHorizontal: 24,
    paddingTop: 22,
    paddingBottom: 18,
    width: "100%",
  },
  closeButton: {
    alignItems: "center",
    borderRadius: 999,
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  header: {
    gap: 8,
    marginBottom: 18,
  },
  headerTopRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
  },
  title: {
    color: Colors.blackColor,
    flex: 1,
    fontSize: 17,
    fontWeight: "600",
    lineHeight: 22,
    textTransform: "uppercase",
  },
  helperText: {
    color: Colors.darkgrayColor,
    fontSize: 12,
    lineHeight: 18,
  },
  inputShell: {
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderColor: Colors.borderColor,
    borderRadius: 14,
    borderWidth: 1,
    flexDirection: "row",
    minHeight: 46,
    paddingHorizontal: 14,
    transition: "border-color 160ms ease",
  },
  inputShellHovered: {
    borderColor: Colors.grayColor,
  },
  input: {
    color: Colors.blackColor,
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    outlineStyle: "none",
    paddingVertical: 0,
  },
  actionsRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 18,
  },
  secondaryButton: {
    alignItems: "center",
    backgroundColor: Colors.whiteColor,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    borderWidth: 1,
    height: 40,
    justifyContent: "center",
    minWidth: 96,
    paddingHorizontal: 16,
    transition: "background-color 160ms ease, border-color 160ms ease",
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.hoveredGray,
    borderColor: Colors.grayColor,
  },
  secondaryButtonText: {
    color: Colors.darkgrayColor,
    fontSize: 13,
    fontWeight: "500",
  },
  primaryButton: {
    alignItems: "center",
    backgroundColor: Colors.bubbleColor,
    borderRadius: 12,
    height: 40,
    justifyContent: "center",
    minWidth: 110,
    paddingHorizontal: 16,
    transition: "opacity 160ms ease, background-color 160ms ease",
  },
  primaryButtonHovered: {
    opacity: 0.92,
  },
  primaryButtonDisabled: {
    opacity: 0.5,
  },
  primaryButtonContent: {
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
    justifyContent: "center",
  },
  primaryButtonText: {
    color: Colors.whiteColor,
    fontSize: 13,
    fontWeight: "600",
  },
});

export default function ClientNutritionPlanExportModal({
  isVisible,
  initialName,
  isSaving = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();
  const [name, setName] = useState("");
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [isCancelHovered, setIsCancelHovered] = useState(false);
  const [isConfirmHovered, setIsConfirmHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setName(String(initialName ?? ""));
  }, [initialName, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const focusId = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);

    return () => clearTimeout(focusId);
  }, [isVisible]);

  const normalizedName = useMemo(
    () => normalizeNutritionPlanExportName(name),
    [name],
  );
  const isConfirmEnabled = normalizedName.length > 0 && !isSaving;

  const handleClose = useCallback(() => {
    if (isSaving) {
      return;
    }

    onClose?.();
  }, [isSaving, onClose]);

  const handleConfirm = useCallback(() => {
    if (!isConfirmEnabled) {
      return;
    }

    onConfirm?.(normalizedName);
  }, [isConfirmEnabled, normalizedName, onConfirm]);

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: handleConfirm,
    onCancel: handleClose,
    isConfirmEnabled,
    isBlocked: isSaving,
  });

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          activeOpacity={1}
          onPress={handleClose}
          style={GlobalStyles.touchableBackground}
        >
          <View />
        </TouchableOpacity>

        <View
          style={[
            styles.modalView,
            { width: screenSize === "small" ? 320 : 400 },
          ]}
        >
          <View style={styles.header}>
            <View style={styles.headerTopRow}>
              <Text style={styles.title}>
                {t(
                  "clientNutritionPlanExportModalTitle-string",
                  "Export nutrition plan",
                )}
              </Text>
              <TouchableOpacity
                activeOpacity={0.8}
                disabled={isSaving}
                onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
                onMouseLeave={() => setIsCloseHovered(false)}
                onPress={handleClose}
                style={styles.closeButton}
              >
                <Ionicons
                  name="close"
                  size={22}
                  color={isCloseHovered ? Colors.darkgrayColor : Colors.grayColor}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.helperText}>
              {t(
                "clientNutritionPlanExportModalHelper-string",
                "Set the name of the plan for your library.",
              )}
            </Text>
          </View>

          <View
            onMouseEnter={!isMobile ? () => setIsInputHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsInputHovered(false) : undefined}
            style={[
              styles.inputShell,
              isInputHovered ? styles.inputShellHovered : null,
            ]}
          >
            <TextInput
              ref={inputRef}
              autoCapitalize="sentences"
              autoCorrect={false}
              blurOnSubmit={false}
              editable={!isSaving}
              maxLength={80}
              onChangeText={setName}
              onSubmitEditing={handleConfirm}
              placeholder={t(
                "clientNutritionPlanExportModalPlaceholder-string",
                "Nutrition plan name",
              )}
              placeholderTextColor={Colors.grayColor}
              returnKeyType="done"
              selectTextOnFocus={true}
              style={styles.input}
              value={name}
            />
          </View>

          <View style={styles.actionsRow}>
            <TouchableOpacity
              activeOpacity={0.86}
              disabled={isSaving}
              onMouseEnter={() => !isMobile && setIsCancelHovered(true)}
              onMouseLeave={() => setIsCancelHovered(false)}
              onPress={handleClose}
            >
              <View
                style={[
                  styles.secondaryButton,
                  isCancelHovered ? styles.secondaryButtonHovered : null,
                ]}
              >
                <Text style={styles.secondaryButtonText}>
                  {t("cancel-string", "Cancel")}
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              activeOpacity={0.9}
              disabled={!isConfirmEnabled}
              onMouseEnter={() => !isMobile && setIsConfirmHovered(true)}
              onMouseLeave={() => setIsConfirmHovered(false)}
              onPress={handleConfirm}
            >
              <View
                style={[
                  styles.primaryButton,
                  isConfirmHovered ? styles.primaryButtonHovered : null,
                  !isConfirmEnabled ? styles.primaryButtonDisabled : null,
                ]}
              >
                <View style={styles.primaryButtonContent}>
                  {isSaving ? (
                    <ActivityIndicator color={Colors.whiteColor} size="small" />
                  ) : null}
                  <Text style={styles.primaryButtonText}>
                    {t("exportToLibrary-string", "Export to library")}
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}
